import { Link } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import { useFavorites } from "@/hooks/useFavorites";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ProductDetail } from "@/mocks/productDetails";

interface ProductCardProps {
  product: ProductDetail;
  viewMode: "grid" | "list";
  liveStock?: number;
  onShowSpecs?: () => void;
}

const categoryLabels: Record<string, string> = {
  "PS5": "PS5",
  "PS4": "PS4",
  "PC": "كمبيوتر ألعاب",
  "Controllers": "أيادي التحكم",
  "Headsets": "سماعات",
};

/* ── Mini stock indicator ── */
function StockIndicator({ stock, loading }: { stock: number | undefined; loading: boolean }) {
  const { primary, hexAlpha } = useThemeColor();
  if (loading || stock === undefined) return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse"
      style={{ background: hexAlpha(primary, 0.08), color: hexAlpha(primary, 0.4), fontFamily: "'Cairo', sans-serif" }}>
      <i className="ri-loader-4-line animate-spin text-[10px]"></i>جاري التحقق
    </span>
  );
  if (stock === 0) return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20" style={{ fontFamily: "'Cairo', sans-serif" }}>
      <i className="ri-close-circle-fill text-[10px]"></i>نفد المخزون
    </span>
  );
  if (stock <= 3) return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20" style={{ fontFamily: "'Cairo', sans-serif" }}>
      <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-ping inline-block flex-shrink-0"></span>
      آخر {stock} {stock === 1 ? "قطعة" : "قطع"}!
    </span>
  );
  if (stock <= 10) return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-400 border border-amber-400/20" style={{ fontFamily: "'Cairo', sans-serif" }}>
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse inline-block flex-shrink-0"></span>
      باقي {stock} فقط
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/20" style={{ fontFamily: "'Cairo', sans-serif" }}>
      <i className="ri-checkbox-circle-fill text-[10px]"></i>متوفر ({stock})
    </span>
  );
}

/* ── Stock bar ── */
function StockBar({ stock, maxStock }: { stock: number; maxStock: number }) {
  const pct = maxStock > 0 ? Math.min(100, Math.round((stock / maxStock) * 100)) : 0;
  const color = stock === 0 ? "#ef4444" : stock <= 3 ? "#ef4444" : stock <= 10 ? "#f59e0b" : "#10b981";
  return (
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40">
      <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: color }}></div>
    </div>
  );
}

export default function ProductCard({ product, viewMode, liveStock, onShowSpecs }: ProductCardProps) {
  const { addItem, items } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { primary, hexAlpha } = useThemeColor();
  const inCart = items.some((i) => i.id === product.id);
  const fav = isFavorite(product.id);

  const stockLoading = liveStock === undefined;
  const stock = liveStock ?? product.stock ?? 0;
  const isOutOfStock = !stockLoading && stock === 0;
  const isLowStock = !stockLoading && stock > 0 && stock <= 5;
  const maxStock = Math.max(stock, 20);

  const handleAdd = () => {
    if (isOutOfStock) return;
    addItem({ id: product.id, nameAr: product.nameAr, price: product.price, image: product.images?.[0] ?? "", category: product.category, stock });
  };

  const handleToggleFav = () => {
    toggleFavorite({ id: product.id, nameAr: product.nameAr, price: product.price, originalPrice: product.originalPrice, image: product.images?.[0] ?? "", category: product.category, badge: product.badge ?? "", rating: product.rating, reviews: product.reviews });
  };

  const discount = product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100) : 0;

  /* ══════════════ LIST VIEW ══════════════ */
  if (viewMode === "list") {
    return (
      <div
        className="bg-[#141414] border border-white/5 rounded-xl overflow-hidden transition-all duration-300 flex gap-0"
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.3); }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.05)"; }}
      >
        {/* Image — click opens specs */}
        <button
          onClick={onShowSpecs}
          className="relative w-40 sm:w-52 shrink-0 bg-[#1a1a1a] cursor-pointer group/img overflow-hidden flex-shrink-0"
        >
          {product.images?.[0] ? (
            <img src={product.images[0]} alt={product.nameAr}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover/img:scale-105" />
          ) : (
            <div className="w-full h-full flex items-center justify-center min-h-[140px]">
              <i className="ri-image-line text-gray-700 text-3xl"></i>
            </div>
          )}
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/40 transition-all duration-300 flex items-center justify-center">
            <span className="opacity-0 group-hover/img:opacity-100 transition-all duration-300 flex flex-col items-center gap-1">
              <i className="ri-eye-line text-white text-xl"></i>
              <span className="text-white text-[10px] font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>المواصفات</span>
            </span>
          </div>
          {isOutOfStock && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif" }}>نفد المخزون</span>
            </div>
          )}
          {product.badge && !isOutOfStock && (
            <span className="absolute top-2 right-2 text-black text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif", background: primary }}>{product.badge}</span>
          )}
          {discount > 0 && !isOutOfStock && (
            <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif" }}>-{discount}%</span>
          )}
          {isLowStock && (
            <span className="absolute bottom-2 right-2 bg-amber-400 text-black text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-black/40 animate-pulse inline-block"></span>باقي {stock} فقط
            </span>
          )}
          {!stockLoading && <StockBar stock={stock} maxStock={maxStock} />}
        </button>

        {/* Info */}
        <div className="flex-1 p-5 flex flex-col justify-between">
          <div>
            <p className="text-gray-500 text-xs mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {categoryLabels[product.category] ?? product.category}
            </p>
            <Link to={`/product/${product.id}`}
              className="block text-white font-bold text-lg mb-2 transition-colors cursor-pointer"
              style={{ fontFamily: "'Cairo', sans-serif" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = primary; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "#fff"; }}>
              {product.nameAr}
            </Link>
            {product.shortDesc && (
              <p className="text-gray-500 text-sm mb-3 line-clamp-2" style={{ fontFamily: "'Cairo', sans-serif" }}>{product.shortDesc}</p>
            )}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-xs" style={{ color: i < product.rating ? primary : "#374151" }}></i>
                ))}
                <span className="text-gray-500 text-xs mr-1" style={{ fontFamily: "'Cairo', sans-serif" }}>({product.reviews})</span>
              </div>
              <StockIndicator stock={liveStock} loading={stockLoading} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <span className="font-black text-xl" style={{ fontFamily: "'Cairo', sans-serif", color: isOutOfStock ? "#6b7280" : primary }}>
                {product.price.toLocaleString()} ر.س
              </span>
              {product.originalPrice > product.price && (
                <span className="text-gray-600 text-sm line-through mr-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {product.originalPrice.toLocaleString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleToggleFav}
                className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-all cursor-pointer ${fav ? "bg-red-500/10 border-red-500/30 text-red-400" : "border-white/10 text-gray-500 hover:text-red-400 hover:border-red-500/30"}`}>
                <i className={`text-sm ${fav ? "ri-heart-fill" : "ri-heart-line"}`}></i>
              </button>
              <button onClick={onShowSpecs}
                className="px-4 py-2 border border-white/10 text-gray-400 text-xs rounded-lg transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Cairo', sans-serif" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.4); (e.currentTarget as HTMLElement).style.color = primary; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.1)"; (e.currentTarget as HTMLElement).style.color = "#9ca3af"; }}>
                المواصفات
              </button>
              {isOutOfStock ? (
                <span className="px-4 py-2 rounded-lg text-xs font-bold bg-white/5 text-gray-600 border border-white/5 whitespace-nowrap cursor-not-allowed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  نفد المخزون
                </span>
              ) : (
                <button onClick={handleAdd} disabled={stockLoading}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 ${inCart ? "bg-green-500 text-white" : "text-black"}`}
                  style={!inCart ? { fontFamily: "'Cairo', sans-serif", background: primary } : { fontFamily: "'Cairo', sans-serif" }}>
                  {inCart ? <><i className="ri-check-line ml-1"></i>في السلة</> : <><i className="ri-shopping-cart-line ml-1"></i>أضف للسلة</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════ GRID VIEW ══════════════ */
  return (
    <div
      className={`bg-[#141414] border rounded-xl overflow-hidden transition-all duration-300 group flex flex-col ${isOutOfStock ? "border-white/5 opacity-80" : "border-white/5"}`}
      onMouseEnter={(e) => { if (!isOutOfStock) (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.3); }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.05)"; }}
    >
      {/* Image — click opens specs */}
      <button
        onClick={onShowSpecs}
        className="block relative w-full h-52 overflow-hidden bg-[#1a1a1a] cursor-pointer text-left"
      >
        {product.images?.[0] ? (
          <img src={product.images[0]} alt={product.nameAr}
            className={`w-full h-full object-cover object-top transition-transform duration-500 ${isOutOfStock ? "grayscale-[40%]" : "group-hover:scale-105"}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="ri-image-line text-gray-700 text-4xl"></i>
          </div>
        )}

        {/* Hover overlay with specs hint */}
        {!isOutOfStock && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-all duration-300 flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100 bg-black/80 backdrop-blur-sm text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-2"
              style={{ fontFamily: "'Cairo', sans-serif", border: `1px solid ${hexAlpha(primary, 0.4)}`, color: primary }}>
              <i className="ri-list-check-3 text-sm"></i>
              عرض المواصفات
            </span>
          </div>
        )}

        {isOutOfStock && (
          <div className="absolute inset-0 bg-black/55 flex items-end justify-center pb-4">
            <span className="bg-red-500 text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap tracking-wide" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-close-circle-line ml-1 text-xs"></i>نفد المخزون
            </span>
          </div>
        )}
        {isLowStock && (
          <div className="absolute bottom-4 right-3">
            <span className="bg-amber-400 text-black text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-black/40 animate-pulse inline-block"></span>باقي {stock} فقط!
            </span>
          </div>
        )}
        {product.badge && !isOutOfStock && (
          <span className="absolute top-3 right-3 text-black text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif", background: primary }}>{product.badge}</span>
        )}
        {discount > 0 && !isOutOfStock && (
          <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif" }}>-{discount}%</span>
        )}
        {/* Favorite button */}
        <button onClick={(e) => { e.stopPropagation(); handleToggleFav(); }}
          className={`absolute bottom-3 left-3 w-8 h-8 flex items-center justify-center rounded-full transition-all cursor-pointer ${fav ? "bg-red-500 text-white" : "bg-black/50 text-gray-400 hover:text-red-400"}`}>
          <i className={`text-sm ${fav ? "ri-heart-fill" : "ri-heart-line"}`}></i>
        </button>
        {!stockLoading && <StockBar stock={stock} maxStock={maxStock} />}
      </button>

      {/* Info */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-1">
          <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
            {categoryLabels[product.category] ?? product.category}
          </p>
          <StockIndicator stock={liveStock} loading={stockLoading} />
        </div>

        <Link to={`/product/${product.id}`}
          className="block text-white font-bold text-base mb-3 flex-1 transition-colors cursor-pointer mt-1"
          style={{ fontFamily: "'Cairo', sans-serif" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = primary; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "#fff"; }}>
          {product.nameAr}
        </Link>

        <div className="flex items-center gap-1 mb-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <i key={i} className="ri-star-fill text-xs" style={{ color: i < product.rating ? primary : "#374151" }}></i>
          ))}
          <span className="text-gray-500 text-xs mr-1" style={{ fontFamily: "'Cairo', sans-serif" }}>({product.reviews})</span>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="font-black text-lg" style={{ fontFamily: "'Cairo', sans-serif", color: isOutOfStock ? "#6b7280" : primary }}>
              {product.price.toLocaleString()} ر.س
            </span>
            {product.originalPrice > product.price && (
              <span className="text-gray-600 text-xs line-through mr-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {product.originalPrice.toLocaleString()}
              </span>
            )}
          </div>
          {isOutOfStock ? (
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 cursor-not-allowed">
              <i className="ri-close-line text-gray-600 text-sm"></i>
            </div>
          ) : (
            <button onClick={handleAdd} disabled={stockLoading}
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer disabled:opacity-50 ${inCart ? "bg-green-500 text-white" : "text-black"}`}
              style={!inCart ? { background: primary } : {}}>
              <i className={inCart ? "ri-check-line text-sm" : "ri-shopping-cart-line text-sm"}></i>
            </button>
          )}
        </div>

        <button onClick={onShowSpecs}
          className="flex items-center justify-center gap-2 w-full border border-white/10 text-gray-400 text-xs py-2 rounded-lg transition-all cursor-pointer"
          style={{ fontFamily: "'Cairo', sans-serif" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.4); (e.currentTarget as HTMLElement).style.color = primary; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.1)"; (e.currentTarget as HTMLElement).style.color = "#9ca3af"; }}>
          <i className="ri-list-check-3 text-sm"></i>
          عرض المواصفات
        </button>
      </div>
    </div>
  );
}
